/* oxlint-disable react/jsx-pascal-case */
/* oxlint-disable @typescript-eslint/no-unsafe-type-assertion -- HTML parser node typing */
import clsx from 'clsx'
import { useContext, useId, type ReactNode } from 'react'
import { Text } from 'react-native'

import type {
  TContentAudioValue,
  TContentBlockquoteValue,
  TContentDetailsValue,
  TContentHeadingValue,
  TContentIframeValue,
  TContentImportantValue,
  TContentLinkValue,
  TContentListValue,
  TContentLiValue,
  TContentPValue,
  TContentQuoteValue,
  TContentSummaryValue,
  TContentTextValue,
  TContentYoutubeValue,
} from './content.types'
import {
  ContentContext,
  ContentContextProvider,
  ContentContextUpdater,
  type TContentContextProviderProps,
} from './ContentContext'
import type { TNode, TNodeTag } from './contentHtmlParser'

export const mergeProps = <PropsA extends TAnyProps, PropsB extends TAnyProps>(
  propsA: PropsA,
  propsB: PropsB,
): PropsA & PropsB => {
  const props: PropsA & PropsB & { className?: string; classNames?: Record<string, string> } = { ...propsA, ...propsB }
  if (propsA && propsB && 'className' in propsA && 'className' in propsB) {
    props.className = clsx(propsB.className, propsA.className)
  }
  if (propsA && propsB && 'classNames' in propsA && 'classNames' in propsB) {
    props.classNames = Object.keys(propsB.classNames || {}).reduce(
      (acc, slot) => {
        acc[slot] = clsx(propsA.classNames?.[slot], propsB.classNames?.[slot])
        return acc
      },
      { ...propsA.classNames },
    )
  }
  return props
}

/**
 * Checks if given node is a {@link TNodeTag} node.
 * It checks if a given node (c) is a TNodeTag node by verifying that it's an object, not null, and has a tag property.
 * If all conditions are true, it returns true, indicating that c is indeed a TNodeTag node.
 * @returns If `c` is a {@link TNodeTag} node.
 */
const isTNodeTag = <T extends string>(c?: TNode | TNodeTag<T>): c is TNodeTag<T> =>
  typeof c === 'object' && c !== null && 'tag' in c

const getSize = (size: string | number | undefined | boolean) =>
  typeof size !== 'undefined' ? Number(size) : undefined

export const ContentNode = ({ node: originalNode }: { node: TNode }): ReactNode => {
  const { Components, props } = useContext(ContentContext)
  const id = useId()
  if (typeof originalNode === 'number' || typeof originalNode === 'string') {
    return <Text>{originalNode}</Text>
  }

  // TOTO Convert style CSS to react Native Style
  const node = originalNode.attrs?.style
    ? (() => {
        const { style: _, ...attrsWithoutStyle } = originalNode.attrs
        return { ...originalNode, attrs: attrsWithoutStyle }
      })()
    : originalNode

  let contentNodes

  if (Array.isArray(node.content)) {
    contentNodes = node.content
  } else if (typeof node.content === 'undefined') {
    contentNodes = undefined
  } else {
    contentNodes = [node.content]
  }

  const children = contentNodes?.map((contentNode, index) => {
    // Override all h2 content to have the same style
    if (
      typeof contentNode !== 'number' &&
      typeof contentNode !== 'string' &&
      node.tag === 'h2' &&
      contentNode.tag !== 'iframe'
    ) {
      contentNode.tag = 'span'
    }
    // oxlint-disable-next-line react/no-array-index-key
    return <ContentNode key={`child-${id}-${index}`} node={contentNode} />
  })

  switch (node.tag) {
    case 'a':
      if (!node.attrs?.href) {
        return (
          <Components.text {...mergeProps(node.attrs as TContentLinkValue, props?.text || {})}>
            {children}
          </Components.text>
        )
      }
      return (
        <Components.link
          {...mergeProps(props?.link || {}, node.attrs as TContentLinkValue)}
          className="text-content-link"
        >
          <ContentContextUpdater
            props={{
              p: { className: 'text-content-link' },
              text: { className: 'text-content-link' },
            }}
          >
            {children}
          </ContentContextUpdater>
        </Components.link>
      )

    case 'br':
      return <Components.nl />

    case 'img': {
      const { width, height, src } = node.attrs || {}
      return <Components.img {...props?.img} source={src.toString()} width={getSize(width)} height={getSize(height)} />
    }

    case 'audio': {
      let src: string | undefined
      const { attrs: rootAttrs, content } = node as TNodeTag<'audio'>
      if (rootAttrs?.src) {
        src = typeof rootAttrs.src === 'string' ? rootAttrs.src : String(rootAttrs.src)
      }
      if (content && content.length > 0) {
        const audioSource = content.find((c) => isTNodeTag<'source'>(c) && c?.tag === 'source')
        const sourceAttrs = isTNodeTag(audioSource) && audioSource.attrs
        if (sourceAttrs && sourceAttrs.src && typeof sourceAttrs.src === 'string') {
          src = sourceAttrs.src
        }
      }
      if (src) {
        return <Components.audio {...mergeProps(node.attrs as TContentAudioValue, props?.audio || {})} source={src} />
      }
      if (content && content.length > 0) {
        // oxlint-disable-next-line react/no-array-index-key
        return content?.map((contentNode, index) => <ContentNode key={`audio-node-${index}`} node={contentNode} />)
      }
      return null
    }

    case 'iframe': {
      const { attrs: { src } = {} } = node as TNodeTag<'iframe'>
      // oxlint-disable-next-line hozana-local/boolean-variable-naming
      const youtubeMatch =
        src && typeof src === 'string' && src.includes('youtu') && /\?v=([\w|-]+)|\/embed\/([\w|-]+)/.exec(src)
      const youtubeId = youtubeMatch ? youtubeMatch[1] || youtubeMatch[2] : undefined
      const isAushaSource = src && typeof src === 'string' && src.includes('ausha.co')
      // oxlint-disable-next-line hozana-local/boolean-variable-naming
      const soundCloudMatch = src && typeof src === 'string' && src.includes('soundcloud.com')
      // oxlint-disable-next-line hozana-local/boolean-variable-naming
      const audioMatch =
        src &&
        typeof src === 'string' &&
        (src.includes('rcf.fr') ||
          src.includes('soundcloud.com') ||
          src.includes('anchor.fm') ||
          src.includes('.mp3') ||
          isAushaSource)

      if (youtubeId) {
        return (
          <Components.youtube
            {...mergeProps(node.attrs as TContentYoutubeValue, props?.youtube || {})}
            youtubeId={youtubeId}
          />
        )
      }
      if (audioMatch) {
        const iframeProps = {
          className: '' as string,
          style: {} as Record<string, number | undefined>,
        }
        if (node.attrs?.height && isAushaSource) {
          iframeProps.className = 'max-[539px]:h-[500px]'
          iframeProps.style.minHeight = Number(node.attrs.height)
        } else if (node.attrs?.height && !soundCloudMatch && !node.attrs?.height.toString().endsWith('%')) {
          const stringHeight = /(\d+)/.exec(node.attrs.height.toString())
          iframeProps.style.height = stringHeight ? Number(stringHeight[0]) : undefined
        }
        return (
          <Components.audio
            {...mergeProps(node.attrs as TContentAudioValue, props?.audio || {})}
            source={src}
            iframeProps={iframeProps}
          />
        )
      }
      if (src) {
        return (
          <Components.iframe
            {...mergeProps(node.attrs as TContentIframeValue, props?.iframe || {})}
            src={src.toString()}
          />
        )
      }
      return null
    }

    case 'p': {
      if (typeof node.attrs?.class === 'string') {
        if (node.attrs.class.includes('hoz_important')) {
          return (
            <Components.important
              {...mergeProps(node.attrs as Omit<TContentImportantValue, 'children'>, props?.important || {})}
            >
              <ContentNode node={{ tag: 'p', content: node.content }} />
            </Components.important>
          )
        }
        if (node.attrs.class.includes('hoz_quote')) {
          return (
            <Components.quote {...mergeProps(node.attrs as Omit<TContentQuoteValue, 'children'>, props?.quote || {})}>
              {children}
            </Components.quote>
          )
        }
      }
      return (
        <Components.p {...mergeProps(node.attrs as Omit<TContentPValue, 'children'>, props?.p || {})}>
          {children}
        </Components.p>
      )
    }

    case 'div': {
      if (typeof node.attrs?.class === 'string') {
        if (node.attrs.class.includes('hoz_important')) {
          return (
            <Components.important
              {...mergeProps(node.attrs as Omit<TContentImportantValue, 'children'>, props?.important || {})}
            >
              {children}
            </Components.important>
          )
        }
        if (node.attrs.class.includes('hoz_quote')) {
          return (
            <Components.quote {...mergeProps(node.attrs as Omit<TContentQuoteValue, 'children'>, props?.quote || {})}>
              {children}
            </Components.quote>
          )
        }
      }
      return children
    }

    case 'summary':
      return (
        <Components.summary {...mergeProps(node.attrs as Omit<TContentSummaryValue, 'children'>, props?.summary || {})}>
          {children}
        </Components.summary>
      )

    case 'strong':
    case 'b':
    case 'big':
    case 'mark':
      return (
        <Components.text {...mergeProps(node.attrs as TContentTextValue, { format: 'bold', ...props?.text })}>
          {children}
        </Components.text>
      )
    case 'pre':
      return (
        <Components.text {...mergeProps(node.attrs as TContentTextValue, { format: 'default', ...props?.text })}>
          {children}
        </Components.text>
      )
    case 'dfn':
    case 'em':
    case 'figcaption':
    case 'i':
    case 'legend':
      return (
        <Components.text {...mergeProps(node.attrs as TContentTextValue, { format: 'italic', ...props?.text })}>
          {children}
        </Components.text>
      )

    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
    case 'h6':
      if (typeof node.attrs?.class === 'string') {
        if (node.attrs.class.includes('hoz_important')) {
          return (
            <Components.important
              {...mergeProps(node.attrs as Omit<TContentImportantValue, 'children'>, props?.important || {})}
            >
              <ContentNode node={{ tag: 'p', content: node.content }} />
            </Components.important>
          )
        }
        if (node.attrs.class.includes('hoz_quote')) {
          return (
            <Components.quote {...mergeProps(node.attrs as Omit<TContentQuoteValue, 'children'>, props?.quote || {})}>
              {children}
            </Components.quote>
          )
        }
      }
      return (
        <Components.heading {...mergeProps(node.attrs as Omit<TContentHeadingValue, 'children'>, props?.heading || {})}>
          {children}
        </Components.heading>
      )

    case 'ol':
      return (
        <Components.list
          {...mergeProps(props?.list || {}, node.attrs as unknown as Omit<TContentListValue, 'listType'>)}
          listType="number"
        >
          {children}
        </Components.list>
      )

    case 'ul':
      return (
        <Components.list
          {...mergeProps(props?.list || {}, node.attrs as unknown as Omit<TContentListValue, 'listType'>)}
          listType="bullet"
        >
          {children}
        </Components.list>
      )

    case 'li':
      return (
        <Components.li {...mergeProps(props?.li || {}, node.attrs as TContentLiValue)}>
          {contentNodes?.map((contentNode, index) => {
            if (typeof contentNode === 'string' || typeof contentNode === 'number')
              return (
                <Components.text
                  // oxlint-disable-next-line react/no-array-index-key
                  key={contentNode || index}
                  {...mergeProps({ format: 'normal', ...props?.text }, node.attrs as TContentTextValue)}
                >
                  <ContentNode node={contentNode} />
                </Components.text>
              )
            // oxlint-disable-next-line react/no-array-index-key
            else return <ContentNode key={`li-${id}-${index}`} node={contentNode} />
          })}
        </Components.li>
      )

    case 'blockquote':
      return (
        <Components.blockquote {...(node.attrs as Omit<TContentBlockquoteValue, 'children'>)}>
          {children}
        </Components.blockquote>
      )

    case 'details': {
      const summaryIndex = contentNodes?.findIndex((c) => isTNodeTag(c) && c.tag === 'summary') ?? -1
      const summaryNode = summaryIndex >= 0 ? (contentNodes?.[summaryIndex] as TNodeTag<'summary'>) : undefined
      const { open, ...detailsAttrs } = (node.attrs || {}) as Omit<TContentDetailsValue, 'children'>
      return (
        <Components.details
          {...mergeProps(detailsAttrs, props?.details || {})}
          open={Boolean(open)}
          summary={summaryNode ? <ContentNode key={`summary-${id}`} node={summaryNode} /> : undefined}
        >
          {children?.filter((_, index) => index !== summaryIndex)}
        </Components.details>
      )
    }

    // TO BE IMPLEMENTED
    case 'embed':
    case 'hr':
    case 'picture':
    case 'abbr':
    case 'address':
    case 'center':
    case 'code':
    case 's':
    case 'samp':
    case 'small':
    case 'span':
    case 'sub':
    case 'sup':
    case 'time':
    case 'u':
    case 'var':
    case 'q':
    case 'cite':
    case 'bdi':
    case 'bdo':
    case 'article':
    case 'aside':
    case 'figure':
    case 'footer':
    case 'header':
    case 'hgroup':
    case 'main':
    case 'search':
    case 'section':
    case 'source':
    case 'body':
      return children
    default:
      return null
  }
}

export type TContentProps = TContentContextProviderProps & { nodes: TNode[] }

export const Content = ({ nodes, ...otherProps }: TContentProps): ReactNode => {
  const id = useId()
  return (
    <ContentContextProvider {...otherProps}>
      {nodes.map((node, index) => (
        // oxlint-disable-next-line react/no-array-index-key
        <ContentNode key={`${id}-${index}`} node={node} />
      ))}
    </ContentContextProvider>
  )
}
